/**
 * Binary logistic regression by iteratively reweighted least squares (IRLS).
 *
 * The maximum-likelihood fit follows R's `glm.fit(family = binomial)` step for step, so
 * the coefficients, standard errors, deviances and even the iteration count agree with R:
 *
 * - start from μ = (y + ½) / 2 and η = logit(μ);
 * - each iteration forms the working response z = η + (y − μ) / (μ(1 − μ)) and weights
 *   w = μ(1 − μ), and solves the weighted least-squares problem by **QR** (not by
 *   inverting XᵀWX);
 * - stop when |Δdeviance| / (|deviance| + 0.1) < 1e-8, after at most 25 iterations;
 * - the covariance matrix is (XᵀWX)⁻¹ from the last iteration's QR, as in `summary.glm`.
 *
 * ## Separation and non-convergence
 *
 * When a predictor (or combination) perfectly or almost perfectly separates the two
 * outcomes, the likelihood has no maximum: the coefficient grows without bound, the
 * standard errors explode, and the Wald z collapses to zero. R reports this only as a
 * warning ("fitted probabilities numerically 0 or 1 occurred"), often alongside
 * apparently successful convergence. Here it is a status the page must show: any fitted
 * probability within 1e-8 of 0 or 1 is flagged as separation, and hitting the iteration
 * limit is flagged as non-convergence.
 *
 * ## Firth's penalised fit
 *
 * Firth's (1993) bias-reduced logistic regression maximises ℓ(β) + ½ log|XᵀWX| (Jeffreys'
 * prior), which has a finite maximum even under separation. It is fitted by Fisher scoring
 * on the modified score X'(y − μ + h(½ − μ)) with step-halving on the penalised
 * likelihood. Wald standard errors are reported; profile penalised-likelihood intervals
 * (as in R's `logistf`) are generally preferable and are not computed.
 */

import { averageRanks } from "@/lib/stats/descriptive";
import {
  chiSquareUpper,
  normalCdf,
  normalQuantile,
} from "@/lib/stats/distributions";
import { qrLeastSquares } from "@/lib/tool-kit/stats-regression/linalg";

const EPSILON = 1e-8;
const MAX_ITERATIONS = 25;
const FIRTH_MAX_ITERATIONS = 100;
/** `glm.fit`'s QR tolerance: min(1e-7, epsilon / 1000). */
const GLM_TOLERANCE = 1e-11;
const THRESH = 30;
/** A fitted probability this close to 0 or 1 is read as separation. */
const SEPARATION_BOUND = 1e-8;

export type LogisticStatus = "ok" | "separation" | "no-convergence";

export interface LogisticCoefficient {
  name: string;
  estimate: number;
  se: number;
  z: number;
  p: number;
  oddsRatio: number;
  ciLow: number;
  ciHigh: number;
  orLow: number;
  orHigh: number;
  aliased: boolean;
}

export interface Classification {
  cutoff: number;
  tp: number;
  fp: number;
  tn: number;
  fn: number;
  accuracy: number;
  sensitivity: number;
  specificity: number;
  ppv: number;
  npv: number;
}

export interface RocPoint {
  fpr: number;
  tpr: number;
}

export interface LogisticReport {
  n: number;
  events: number;
  nonEvents: number;
  rank: number;
  firth: boolean;
  status: LogisticStatus;
  iterations: number;
  converged: boolean;
  coefficients: LogisticCoefficient[];
  /** (XᵀWX)⁻¹ in the order intercept, predictors. */
  covariance: number[][];
  fitted: number[];
  /** −2 log-likelihood of the fitted model (deviance, for binary data). */
  deviance: number;
  nullDeviance: number;
  /** Likelihood-ratio χ² for the model against intercept-only; penalised for Firth. */
  lrChi2: number;
  lrDf: number;
  lrP: number;
  aic: number;
  bic: number;
  mcFadden: number;
  coxSnell: number;
  nagelkerke: number;
  tjur: number;
  auc: number;
  roc: RocPoint[];
  level: number;
  /** Largest absolute Wald standard error among estimated coefficients. */
  maxStandardError: number;
  /** Events per estimated predictor (the smaller class over predictors). */
  eventsPerVariable: number;
}

function linkInverse(eta: number): number {
  let tmp: number;
  if (eta < -THRESH) {
    tmp = Number.EPSILON;
  } else if (eta > THRESH) {
    tmp = 1 / Number.EPSILON;
  } else {
    tmp = Math.exp(eta);
  }
  return tmp / (1 + tmp);
}

function muEta(eta: number): number {
  if (eta > THRESH || eta < -THRESH) {
    return Number.EPSILON;
  }
  const e = Math.exp(eta);
  return e / (1 + e) ** 2;
}

function binomialDeviance(y: readonly number[], mu: readonly number[]): number {
  let total = 0;
  for (const [i, outcome] of y.entries()) {
    total += -2 * Math.log(outcome === 1 ? mu[i] : 1 - mu[i]);
  }
  return total;
}

function logLikelihood(y: readonly number[], mu: readonly number[]): number {
  return -binomialDeviance(y, mu) / 2;
}

function linearPredictor(
  columns: readonly (readonly number[])[],
  beta: readonly number[],
  n: number
): number[] {
  const eta = new Array<number>(n).fill(0);
  for (const [j, column] of columns.entries()) {
    const b = Number.isNaN(beta[j]) ? 0 : beta[j];
    if (b !== 0) {
      for (let i = 0; i < n; i++) {
        eta[i] += column[i] * b;
      }
    }
  }
  return eta;
}

interface Fit {
  beta: number[];
  mu: number[];
  covariance: number[][];
  aliased: boolean[];
  rank: number;
  iterations: number;
  converged: boolean;
}

/** Weighted least squares for one IRLS step. */
function weightedStep(
  columns: readonly (readonly number[])[],
  z: readonly number[],
  w: readonly number[]
) {
  const scaled = columns.map((column) => column.map((v, i) => v * w[i]));
  return qrLeastSquares(
    scaled,
    z.map((v, i) => v * w[i]),
    GLM_TOLERANCE
  );
}

/** Maximum likelihood, mirroring `glm.fit`. */
function fitMaximumLikelihood(
  columns: readonly (readonly number[])[],
  y: readonly number[]
): Fit {
  const n = y.length;
  let mu = y.map((value) => (value + 0.5) / 2);
  let eta = mu.map((value) => Math.log(value / (1 - value)));
  let devOld = binomialDeviance(y, mu);
  let beta = new Array<number>(columns.length).fill(0);
  let last = weightedStep(
    columns,
    eta,
    mu.map(() => 1)
  );
  let converged = false;
  let iterations = 0;
  for (let iter = 1; iter <= MAX_ITERATIONS; iter++) {
    iterations = iter;
    const mueta = eta.map(muEta);
    const w = mu.map((m, i) =>
      Math.sqrt((mueta[i] * mueta[i]) / (m * (1 - m)))
    );
    const z = eta.map((e, i) => e + (y[i] - mu[i]) / mueta[i]);
    last = weightedStep(columns, z, w);
    let start = last.coefficients;
    eta = linearPredictor(columns, start, n);
    mu = eta.map(linkInverse);
    let dev = binomialDeviance(y, mu);
    // Step-halve towards the previous coefficients if the deviance is not finite.
    let halvings = 0;
    while (!Number.isFinite(dev) && iter > 1 && halvings < MAX_ITERATIONS) {
      start = start.map((b, j) => (b + beta[j]) / 2);
      eta = linearPredictor(columns, start, n);
      mu = eta.map(linkInverse);
      dev = binomialDeviance(y, mu);
      halvings += 1;
    }
    beta = start.map((b) => (Number.isNaN(b) ? Number.NaN : b));
    if (Math.abs(dev - devOld) / (Math.abs(dev) + 0.1) < EPSILON) {
      converged = true;
      break;
    }
    devOld = dev;
  }
  return {
    beta,
    mu,
    covariance: last.covariance,
    aliased: last.aliased,
    rank: last.rank,
    iterations,
    converged,
  };
}

function penalisedLogLikelihood(
  columns: readonly (readonly number[])[],
  y: readonly number[],
  beta: readonly number[]
): number {
  const n = y.length;
  const eta = linearPredictor(columns, beta, n);
  const mu = eta.map(linkInverse);
  const w = mu.map((m) => Math.sqrt(m * (1 - m)));
  const gram = weightedStep(columns, new Array<number>(n).fill(0), w);
  return logLikelihood(y, mu) + gram.logDetGram / 2;
}

/** Firth's bias-reduced fit by Fisher scoring with step-halving. */
function fitFirth(
  columns: readonly (readonly number[])[],
  y: readonly number[]
): Fit {
  const n = y.length;
  let beta = new Array<number>(columns.length).fill(0);
  let converged = false;
  let iterations = 0;
  let current = penalisedLogLikelihood(columns, y, beta);
  for (let iter = 1; iter <= FIRTH_MAX_ITERATIONS; iter++) {
    iterations = iter;
    const eta = linearPredictor(columns, beta, n);
    const mu = eta.map(linkInverse);
    const w = mu.map((m) => m * (1 - m));
    const root = w.map(Math.sqrt);
    const hat = weightedStep(
      columns,
      new Array<number>(n).fill(0),
      root
    ).leverage;
    const zStar = eta.map(
      (e, i) => e + (y[i] - mu[i] + hat[i] * (0.5 - mu[i])) / w[i]
    );
    const target = weightedStep(columns, zStar, root).coefficients.map((b) =>
      Number.isNaN(b) ? 0 : b
    );
    const step = target.map((b, j) => b - beta[j]);
    let scale = 1;
    let candidate = target;
    let value = penalisedLogLikelihood(columns, y, candidate);
    for (let h = 0; h < 30 && value < current - 1e-12; h++) {
      scale /= 2;
      candidate = beta.map((b, j) => b + scale * step[j]);
      value = penalisedLogLikelihood(columns, y, candidate);
    }
    const size = Math.max(...step.map((s) => Math.abs(scale * s)));
    beta = candidate;
    current = value;
    if (size < 1e-9) {
      converged = true;
      break;
    }
  }
  const eta = linearPredictor(columns, beta, n);
  const mu = eta.map(linkInverse);
  const final = weightedStep(
    columns,
    new Array<number>(n).fill(0),
    mu.map((m) => Math.sqrt(m * (1 - m)))
  );
  return {
    beta: beta.map((b, j) => (final.aliased[j] ? Number.NaN : b)),
    mu,
    covariance: final.covariance,
    aliased: final.aliased,
    rank: final.rank,
    iterations,
    converged,
  };
}

function twoSidedP(z: number): number {
  return Number.isFinite(z) ? 2 * (1 - normalCdf(Math.abs(z))) : 0;
}

/** Area under the ROC curve: P(score of an event > score of a non-event), ties ½. */
export function areaUnderCurve(
  y: readonly number[],
  scores: readonly number[]
): number {
  const ranks = averageRanks(scores);
  const events = y.reduce((a, b) => a + b, 0);
  const nonEvents = y.length - events;
  if (events === 0 || nonEvents === 0) {
    return Number.NaN;
  }
  let rankSum = 0;
  for (const [i, outcome] of y.entries()) {
    if (outcome === 1) {
      rankSum += ranks[i];
    }
  }
  return (rankSum - (events * (events + 1)) / 2) / (events * nonEvents);
}

const MAX_ROC_POINTS = 200;

/** ROC curve points from the lowest cutoff (everything positive) to the highest. */
export function rocCurve(
  y: readonly number[],
  scores: readonly number[]
): RocPoint[] {
  const events = y.reduce((a, b) => a + b, 0);
  const nonEvents = y.length - events;
  const order = scores
    .map((score, i) => ({ score, outcome: y[i] }))
    .sort((a, b) => b.score - a.score);
  const points: RocPoint[] = [{ fpr: 0, tpr: 0 }];
  let tp = 0;
  let fp = 0;
  for (const [i, item] of order.entries()) {
    if (item.outcome === 1) {
      tp += 1;
    } else {
      fp += 1;
    }
    // Emit a point only where the score changes, so ties form one diagonal step.
    if (i === order.length - 1 || order[i + 1].score !== item.score) {
      points.push({ fpr: fp / nonEvents, tpr: tp / events });
    }
  }
  if (points.length <= MAX_ROC_POINTS) {
    return points;
  }
  const stride = Math.ceil(points.length / MAX_ROC_POINTS);
  return points.filter((_, i) => i % stride === 0 || i === points.length - 1);
}

export function classify(
  y: readonly number[],
  probabilities: readonly number[],
  cutoff: number
): Classification {
  let tp = 0;
  let fp = 0;
  let tn = 0;
  let fn = 0;
  for (const [i, outcome] of y.entries()) {
    const predicted = probabilities[i] >= cutoff;
    if (outcome === 1) {
      tp += predicted ? 1 : 0;
      fn += predicted ? 0 : 1;
    } else {
      fp += predicted ? 1 : 0;
      tn += predicted ? 0 : 1;
    }
  }
  const ratio = (a: number, b: number) => (b === 0 ? Number.NaN : a / b);
  return {
    cutoff,
    tp,
    fp,
    tn,
    fn,
    accuracy: (tp + tn) / y.length,
    sensitivity: ratio(tp, tp + fn),
    specificity: ratio(tn, tn + fp),
    ppv: ratio(tp, tp + fp),
    npv: ratio(tn, tn + fn),
  };
}

function coefficientRows(
  names: readonly string[],
  fit: Fit,
  level: number
): LogisticCoefficient[] {
  const crit = normalQuantile(1 - (1 - level) / 2);
  return fit.beta.map((estimate, j) => {
    const aliased = fit.aliased[j];
    const se = aliased ? Number.NaN : Math.sqrt(fit.covariance[j][j]);
    const z = estimate / se;
    const low = estimate - crit * se;
    const high = estimate + crit * se;
    return {
      name: names[j],
      estimate,
      se,
      z,
      p: aliased ? Number.NaN : twoSidedP(z),
      oddsRatio: Math.exp(estimate),
      ciLow: low,
      ciHigh: high,
      orLow: Math.exp(low),
      orHigh: Math.exp(high),
      aliased,
    };
  });
}

/**
 * Fit a logistic regression. `y` holds 0/1 outcomes; `names` has one entry per column
 * including the intercept first. Returns null when a class is empty or n is too small.
 */
export function logisticReport(
  names: readonly string[],
  predictors: readonly (readonly number[])[],
  y: readonly number[],
  options: { level?: number; firth?: boolean } = {}
): LogisticReport | null {
  const n = y.length;
  const events = y.reduce((a, b) => a + b, 0);
  const k = predictors.length;
  if (k < 1 || events === 0 || events === n || n < k + 2) {
    return null;
  }
  const level = options.level ?? 0.95;
  const firth = options.firth ?? false;
  const columns = [new Array<number>(n).fill(1), ...predictors];
  const fit = firth ? fitFirth(columns, y) : fitMaximumLikelihood(columns, y);
  const coefficients = coefficientRows(names, fit, level);

  const deviance = binomialDeviance(y, fit.mu);
  const ll = -deviance / 2;
  const ll0 =
    events * Math.log(events / n) + (n - events) * Math.log(1 - events / n);
  const nullDeviance = -2 * ll0;
  const lrDf = fit.rank - 1;
  let lrChi2 = nullDeviance - deviance;
  if (firth) {
    // Penalised LR: the intercept-only Firth fit has μ₀ = (n₁ + ½) / (n + 1).
    const mu0 = (events + 0.5) / (n + 1);
    const pll0 =
      events * Math.log(mu0) +
      (n - events) * Math.log(1 - mu0) +
      0.5 * Math.log(n * mu0 * (1 - mu0));
    const pllFull = ll + fitLogDetHalf(columns, fit.mu);
    lrChi2 = 2 * (pllFull - pll0);
  }
  const coxSnell = 1 - Math.exp((2 * (ll0 - ll)) / n);
  const eventScores = fit.mu.filter((_, i) => y[i] === 1);
  const otherScores = fit.mu.filter((_, i) => y[i] === 0);
  const mean = (values: number[]) =>
    values.reduce((a, b) => a + b, 0) / values.length;
  const bounded = fit.mu.some(
    (m) => m < SEPARATION_BOUND || m > 1 - SEPARATION_BOUND
  );
  let status: LogisticStatus = "ok";
  if (!firth && bounded) {
    status = "separation";
  } else if (!fit.converged) {
    status = "no-convergence";
  }
  return {
    n,
    events,
    nonEvents: n - events,
    rank: fit.rank,
    firth,
    status,
    iterations: fit.iterations,
    converged: fit.converged,
    coefficients,
    covariance: fit.covariance,
    fitted: fit.mu,
    deviance,
    nullDeviance,
    lrChi2,
    lrDf,
    lrP: lrDf > 0 ? chiSquareUpper(Math.max(0, lrChi2), lrDf) : Number.NaN,
    aic: deviance + 2 * fit.rank,
    bic: deviance + Math.log(n) * fit.rank,
    mcFadden: 1 - ll / ll0,
    coxSnell,
    nagelkerke: coxSnell / (1 - Math.exp((2 * ll0) / n)),
    tjur: mean(eventScores) - mean(otherScores),
    auc: areaUnderCurve(y, fit.mu),
    roc: rocCurve(y, fit.mu),
    level,
    maxStandardError: Math.max(
      ...coefficients.filter((c) => !c.aliased).map((c) => c.se)
    ),
    eventsPerVariable: Math.min(events, n - events) / k,
  };
}

/** ½ log|XᵀWX| at the fitted probabilities. */
function fitLogDetHalf(
  columns: readonly (readonly number[])[],
  mu: readonly number[]
): number {
  const root = mu.map((m) => Math.sqrt(m * (1 - m)));
  return (
    weightedStep(columns, new Array<number>(mu.length).fill(0), root)
      .logDetGram / 2
  );
}

export interface ProbabilityPrediction {
  linearPredictor: number;
  probability: number;
  odds: number;
  /** Wald interval for the probability, built on the log-odds scale. */
  interval: [number, number];
}

/** Predicted probability at new predictor values, with a Wald interval via the logit. */
export function predictProbability(
  report: LogisticReport,
  values: readonly number[]
): ProbabilityPrediction {
  const x = [1, ...values];
  let eta = 0;
  let variance = 0;
  for (let a = 0; a < x.length; a++) {
    if (report.coefficients[a].aliased) {
      continue;
    }
    eta += report.coefficients[a].estimate * x[a];
    for (let b = 0; b < x.length; b++) {
      if (!report.coefficients[b].aliased) {
        variance += x[a] * report.covariance[a][b] * x[b];
      }
    }
  }
  const crit = normalQuantile(1 - (1 - report.level) / 2);
  const half = crit * Math.sqrt(variance);
  const probability = linkInverse(eta);
  return {
    linearPredictor: eta,
    probability,
    odds: Math.exp(eta),
    interval: [linkInverse(eta - half), linkInverse(eta + half)],
  };
}
